import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Inbox,
  Search,
  Download,
  FileDown,
  RefreshCw,
  Calendar,
  TrendingUp,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { cn } from '../lib/cn';

interface PassiveInvoice {
  uuid: string | null;
  filename: string | null;
  marking: string | null;
  received_at: string | null;
  supplier_name: string | null;
  supplier_vat: string | null;
  number: string | null;
  date: string | null;
  doc_type: string | null;
  total: number | null;
}

const docTypeLabels: Record<string, string> = {
  TD01: 'Fattura',
  TD02: 'Acconto',
  TD03: 'Acconto parcella',
  TD04: 'Nota di credito',
  TD05: 'Nota di debito',
  TD06: 'Parcella',
  TD24: 'Fattura differita',
  TD25: 'Fattura differita',
};

export default function PassiveInvoicesPage() {
  const { addNotification } = useNotifications();

  const [invoices, setInvoices] = useState<PassiveInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    const { data, error } = await supabase.functions.invoke('passive-invoices-list', { body: {} });
    if (error) {
      const msg = (data as any)?.error || error.message || 'Errore nel recupero delle fatture passive';
      setErrorMsg(msg);
      addNotification({ type: 'error', title: 'Errore', message: msg });
    } else if ((data as any)?.error) {
      setErrorMsg((data as any).error);
      addNotification({ type: 'error', title: 'Errore A-Cube', message: (data as any).error });
    } else {
      setInvoices(((data as any)?.invoices ?? []) as PassiveInvoice[]);
      setTruncated(Boolean((data as any)?.truncated));
    }
    setLoading(false);
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return invoices;
    return invoices.filter(inv =>
      [inv.supplier_name, inv.supplier_vat, inv.number, inv.filename]
        .some(v => v?.toLowerCase().includes(q))
    );
  }, [invoices, search]);

  const stats = useMemo(() => ({
    count: filtered.length,
    total: filtered.reduce((sum, inv) => sum + (inv.total || 0), 0),
  }), [filtered]);

  const handleDownload = async (inv: PassiveInvoice, format: 'xml' | 'pdf' = 'xml') => {
    if (!inv.uuid) return;
    setDownloadingKey(`${inv.uuid}:${format}`);
    try {
      const { data, error } = await supabase.functions.invoke('passive-invoices-list', {
        body: { uuid: inv.uuid, download: true, format },
      });
      if (error || (data as any)?.error || !(data as any)?.base64) {
        throw new Error((data as any)?.error || error?.message || 'Download non riuscito');
      }
      const b64 = (data as any).base64 as string;
      const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
      const mime = format === 'pdf' ? 'application/pdf' : 'application/octet-stream';
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const baseName = (inv.filename || `${inv.uuid}.xml`).replace(/\.(xml|p7m)$/i, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'pdf' ? `${baseName}.pdf` : (inv.filename || `${inv.uuid}.xml`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      addNotification({ type: 'error', title: 'Errore', message: (e as Error).message });
    } finally {
      setDownloadingKey(null);
    }
  };

  const markingBadge = (marking: string | null) => {
    const m = (marking || '').toLowerCase();
    if (m.includes('quarant')) return { label: 'Quarantena', cls: 'bg-red-500/10 text-red-500 border-red-500/20' };
    if (m.includes('receiv') || m.includes('ricevut')) return { label: 'Ricevuta', cls: 'bg-green-500/10 text-green-500 border-green-500/20' };
    return { label: marking || 'N/D', cls: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20' };
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900">Passività</h1>
          <p className="text-sm text-neutral-400 mt-1">Fatture passive ricevute dai fornitori tramite SDI (A-Cube)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load()} className="h-10">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 md:gap-6">
        {[
          { label: 'Totale Ricevuto', value: formatCurrency(stats.total), icon: TrendingUp, color: 'text-pink-500' },
          { label: 'Fatture Passive', value: stats.count, icon: Inbox, color: 'text-blue-500' },
        ].map((kpi, i) => (
          <Card key={i} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] md:text-sm font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
              </div>
              <div className="text-sm md:text-2xl font-bold text-gray-900 truncate">{kpi.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card className="p-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <Input
            placeholder="Cerca per fornitore, P.IVA, numero..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-10 md:h-12 border-none bg-transparent focus-visible:ring-0 text-base"
          />
        </div>
      </Card>

      {errorMsg && (
        <Card className="p-4 border-red-200 bg-red-50">
          <div className="flex items-start gap-3 text-sm text-red-700">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Impossibile caricare le fatture passive</p>
              <p className="mt-1 text-red-600">{errorMsg}</p>
            </div>
          </div>
        </Card>
      )}

      {truncated && (
        <p className="text-xs text-amber-600">
          Elenco troncato: mostrate solo le prime pagine. Affina la ricerca o contatta l'amministratore.
        </p>
      )}

      {/* List */}
      <div className="grid grid-cols-1 gap-4">
        {loading && invoices.length === 0 ? (
          [...Array(4)].map((_, i) => <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 rounded-xl">
            <Inbox className="w-12 h-12 text-neutral-600 mx-auto mb-4 opacity-20" />
            <p className="text-neutral-400">Nessuna fattura passiva trovata</p>
          </div>
        ) : (
          filtered.map((inv) => {
            const badge = markingBadge(inv.marking);
            return (
              <Card
                key={inv.uuid || inv.filename}
                className="group p-4 hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-pink-500 bg-white relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1 h-0 group-hover:h-full bg-pink-500 transition-all duration-300" />

                <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
                  <div className="flex-1 min-w-0 basis-1/2 sm:basis-auto sm:pr-6">
                    <h3 className="font-bold text-base sm:text-lg text-gray-900 truncate">
                      {inv.supplier_name || <span className="text-neutral-500 italic">Fornitore sconosciuto</span>}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <div className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border', badge.cls)}>
                        {badge.label}
                      </div>
                      {inv.doc_type && (
                        <span className="text-[10px] text-neutral-500 font-medium uppercase tracking-tight">
                          {docTypeLabels[inv.doc_type] || inv.doc_type}
                        </span>
                      )}
                      {inv.number && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 font-semibold">
                          N. {inv.number}
                        </span>
                      )}
                      {inv.supplier_vat && (
                        <span className="text-[10px] text-neutral-400">{inv.supplier_vat}</span>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-lg font-black text-gray-900">{formatCurrency(inv.total)}</div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-pink-600 hover:bg-pink-50 rounded-full disabled:opacity-40"
                        title="Scarica XML/P7M"
                        disabled={!inv.uuid || !!downloadingKey}
                        onClick={() => handleDownload(inv, 'xml')}
                      >
                        {downloadingKey === `${inv.uuid}:xml`
                          ? <RefreshCw className="w-4 h-4 animate-spin" />
                          : <Download className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-pink-600 hover:bg-pink-50 rounded-full disabled:opacity-40"
                        title="Scarica PDF leggibile"
                        disabled={!inv.uuid || !!downloadingKey}
                        onClick={() => handleDownload(inv, 'pdf')}
                      >
                        {downloadingKey === `${inv.uuid}:pdf`
                          ? <RefreshCw className="w-4 h-4 animate-spin" />
                          : <FileDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3">
                  <div className="flex items-center text-sm text-neutral-400 min-w-0">
                    <Calendar className="w-4 h-4 mr-2 text-neutral-500 flex-shrink-0" />
                    <span>Data doc.: {inv.date ? formatDate(inv.date) : '—'}</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-400">
                    <Inbox className="w-4 h-4 mr-2 text-neutral-500 flex-shrink-0" />
                    <span>Ricevuta: {inv.received_at ? formatDate(inv.received_at) : '—'}</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-400 min-w-0">
                    <FileText className="w-4 h-4 mr-2 text-neutral-500 flex-shrink-0" />
                    <span className="truncate" title={inv.filename || undefined}>{inv.filename || '—'}</span>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
